import type { PatientResponse } from '../../api/generated';
import type { GanttSeries } from './types';

/** A patient booked into a plan, resolved to a name for display. */
export interface PlanPatient {
  patientId: string;
  name: string;
}

// Only `name` is read, so this accepts the patients slice's record as-is, and a
// bare `{ name }` literal in tests. Picked off `PatientResponse` rather than
// restated, so renaming that field upstream is a compile error here instead of
// silently resolving every patient to a blank.
//
// Deliberately wider than the slice's own type, which is `Record<string,
// PatientResponse>` with `noUncheckedIndexedAccess` off: a missing id really can
// come back undefined, so the optional chaining below is load-bearing.
type PatientNameSource = Record<string, Pick<PatientResponse, 'name'> | undefined>;

// Real bookings per treatment plan, in the order the row lists them. Shadow
// seats are excluded throughout: they hold capacity rather than people, so no
// move or removal warning should name one.
//
// Both the move flow and the removal flow ask who a plan affects, so they share
// this one derivation rather than each filtering the row themselves.
export function buildPatientsByPlanId(
  series: GanttSeries[],
  patientsById: PatientNameSource
): ReadonlyMap<string, PlanPatient[]> {
  const byPlan = new Map<string, PlanPatient[]>();

  for (const s of series) {
    for (const group of s.groups) {
      for (const booking of group.assignedPatients ?? []) {
        if (booking.shadow) continue;
        const patients = byPlan.get(s.treatmentPlanId) ?? [];
        if (patients.some((p) => p.patientId === booking.patientId)) continue;
        patients.push({
          patientId: booking.patientId,
          name: patientsById[booking.patientId]?.name ?? ''
        });
        byPlan.set(s.treatmentPlanId, patients);
      }
    }
  }

  return byPlan;
}

// Distinct patients across several plans, in the order the plans are given.
// Deduped on id rather than name, so two patients sharing a name both stay
// listed rather than one silently vanishing from a removal warning.
export function collectPatientsForPlans(
  treatmentPlanIds: string[],
  patientsByPlanId: ReadonlyMap<string, PlanPatient[]>
): PlanPatient[] {
  const seen = new Set<string>();
  const patients: PlanPatient[] = [];

  for (const id of treatmentPlanIds) {
    for (const patient of patientsByPlanId.get(id) ?? []) {
      if (seen.has(patient.patientId)) continue;
      seen.add(patient.patientId);
      patients.push(patient);
    }
  }

  return patients;
}
